import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../../components/table';
import TablePager from '../../components/TablePager';
import { FianzaView } from '../../components/FianzaView';
import { ChevronRightIcon, ChevronDownIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import {
  aggregateProveedores,
  computeConvenioTotals,
  dueLabel,
  formatFechaCorta,
  getFianzaStatus,
} from '../../utils/convenioCompliance';
import { formatNumeroFianza } from '../../utils/calculations';
import { ESTADO_FIANZA } from '../../utils/fianzaEventos';
import {
  ConvenioPicker,
  EmptyScope,
  StatTile,
  TODOS,
  fmtMXN,
  montoContrato,
  useConvenioScope,
} from './convenioScope';

const perPage = 10;
const SIN_PROYECTO = '__sin_proyecto__';
const DIAS = [30, 60, 90];

const finMs = (f) => f?.finVigenciaFianza?.toMillis?.() || f?.finVigenciaFianza || 0;

/**
 * Cobertura por proyecto — the convenio's obra/proyecto view: what each project
 * has contracted, what is actually guaranteed today, and what expires next.
 *
 * A convenio contract belongs to exactly one proyecto (or to none, which gets
 * its own bucket instead of being hidden). Contracts are grouped first, then
 * run through aggregateProveedores so the counting rules are identical to the
 * other convenio surfaces.
 */
export function CoberturaPorProyecto() {
  const navigate = useNavigate();
  const [convenioId, setConvenioId] = useState(TODOS);
  const [page, setPage] = useState(1);
  // Tres niveles independientes: proyecto → fiado → contrato → fianzas.
  const [expanded, setExpanded] = useState(() => new Set());
  const [expandedProveedor, setExpandedProveedor] = useState(() => new Set());
  const [expandedContrato, setExpandedContrato] = useState(() => new Set());
  const [selectedFianza, setSelectedFianza] = useState(null);
  const { options, grupos, contractsById, isAll, loading } = useConvenioScope(convenioId);

  useEffect(() => {
    setPage(1);
    setExpanded(new Set());
    setExpandedProveedor(new Set());
    setExpandedContrato(new Set());
  }, [convenioId]);

  const rows = useMemo(() => {
    const now = Date.now();
    const out = [];
    grupos.forEach((g) => {
      const porProyecto = new Map();
      g.contracts.forEach((c) => {
        const pid = c.proyectoId || SIN_PROYECTO;
        if (!porProyecto.has(pid)) porProyecto.set(pid, []);
        porProyecto.get(pid).push(c);
      });

      porProyecto.forEach((contracts, pid) => {
        const proveedores = aggregateProveedores(contracts, g.beneficiariosById, g.proyectosById);
        const totals = computeConvenioTotals(proveedores);
        const beneficiarios = new Set();
        contracts.forEach((c) => {
          const nombre = g.beneficiariosById[c.beneficiarioId]?.nombre;
          if (nombre) beneficiarios.add(nombre);
        });

        // Expiring cover: only fianzas that are alive and vigentes today. A
        // vencida is already a gap, counted on its own column.
        const porVencer = { 30: 0, 60: 0, 90: 0 };
        let proximaVence = null;
        proveedores.forEach((p) => {
          p.emitidas.forEach((f) => {
            if (f._status.estado !== ESTADO_FIANZA.ACTIVA) return;
            if (f._status.code !== 'vigente') return;
            const fin = finMs(f);
            if (!fin || fin <= now) return;
            const dias = Math.ceil((fin - now) / 86400000);
            DIAS.forEach((limite) => {
              if (dias <= limite) porVencer[limite] += 1;
            });
            if (proximaVence == null || fin < proximaVence) proximaVence = fin;
          });
        });

        const atrasadas = proveedores.reduce((s, p) => s + p.counts.overdue, 0);
        out.push({
          key: `${g.id}:${pid}`,
          convenioId: g.id,
          convenioNombre: g.nombre,
          proyecto:
            pid === SIN_PROYECTO ? 'Sin proyecto' : g.proyectosById[pid]?.nombre || 'Proyecto sin nombre',
          beneficiarios: Array.from(beneficiarios),
          contracts,
          proveedores,
          totals,
          atrasadas,
          porVencer,
          proximaVence,
          contratado: contracts.reduce((s, c) => s + montoContrato(c), 0),
          garantizado: totals.montoTotal - totals.montoPendiente,
        });
      });
    });
    return out.sort((a, b) => {
      if (a.atrasadas !== b.atrasadas) return b.atrasadas - a.atrasadas;
      return b.contratado - a.contratado;
    });
  }, [grupos]);

  const totales = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          proyectos: acc.proyectos + 1,
          contratado: acc.contratado + r.contratado,
          garantizado: acc.garantizado + r.garantizado,
          porEmitir: acc.porEmitir + r.totals.fianzasPorEmitir,
          atrasadas: acc.atrasadas + r.atrasadas,
          vence90: acc.vence90 + r.porVencer[90],
        }),
        { proyectos: 0, contratado: 0, garantizado: 0, porEmitir: 0, atrasadas: 0, vence90: 0 }
      ),
    [rows]
  );

  const toggleIn = (setter) => (key) =>
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const toggle = toggleIn(setExpanded);
  const toggleProveedor = toggleIn(setExpandedProveedor);
  const toggleContrato = toggleIn(setExpandedContrato);

  const openFianza = (fianza, contrato) =>
    setSelectedFianza({
      ...fianza,
      contratoId: fianza.contratoId || contrato.id,
      _contrato: contrato,
    });

  const paged = rows.slice((page - 1) * perPage, page * perPage);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <Heading level={2} className="text-xl text-azul-surelia">Cobertura por proyecto</Heading>
          <Text className="text-sm text-azul-surelia/70">
            Monto contratado contra monto garantizado por proyecto del convenio
          </Text>
        </div>
        <ConvenioPicker value={convenioId} onChange={setConvenioId} options={options} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[240px]">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-azul-surelia"></div>
        </div>
      ) : rows.length === 0 ? (
        <EmptyScope>Sin proyectos con contratos en los convenios.</EmptyScope>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <StatTile label="Proyectos" value={totales.proyectos} />
            <StatTile label="Contratado" value={fmtMXN(totales.contratado)} />
            <StatTile label="Garantizado" value={fmtMXN(totales.garantizado)} sub="fianzas emitidas vivas" />
            <StatTile
              label="Atrasadas"
              value={totales.atrasadas}
              sub={`${totales.porEmitir} por emitir`}
              alert={totales.atrasadas > 0}
            />
            <StatTile label="Vencen en 90 días" value={totales.vence90} />
          </div>

          <div className="overflow-x-auto border border-azul-surelia/15 rounded-lg">
            <Table>
              <TableHead className="bg-azul-surelia text-white">
                <TableRow>
                  <TableHeader className="text-white w-8"></TableHeader>
                  <TableHeader className="text-white">Proyecto</TableHeader>
                  {isAll && <TableHeader className="text-white">Convenio</TableHeader>}
                  <TableHeader className="text-white">Beneficiario</TableHeader>
                  <TableHeader className="text-white text-right">Contratos</TableHeader>
                  <TableHeader className="text-white text-right">Contratado</TableHeader>
                  <TableHeader className="text-white text-right">Garantizado</TableHeader>
                  <TableHeader className="text-white text-right">Por emitir</TableHeader>
                  <TableHeader className="text-white text-right">Atrasadas</TableHeader>
                  <TableHeader className="text-white text-right">30/60/90</TableHeader>
                </TableRow>
              </TableHead>
              <TableBody>
                {paged.map((r) => {
                  const isOpen = expanded.has(r.key);
                  const colSpan = isAll ? 10 : 9;
                  return (
                    <React.Fragment key={r.key}>
                      <TableRow className="cursor-pointer hover:bg-azul-surelia/5" onClick={() => toggle(r.key)}>
                        <TableCell className="py-3 pr-0">
                          {isOpen ? (
                            <ChevronDownIcon className="w-4 h-4 text-zinc-400" />
                          ) : (
                            <ChevronRightIcon className="w-4 h-4 text-zinc-400" />
                          )}
                        </TableCell>
                        <TableCell className="py-3">
                          <Text className="text-sm font-medium text-azul-surelia truncate max-w-[240px]" title={r.proyecto}>
                            {r.proyecto}
                          </Text>
                          {r.proximaVence != null && (
                            <Text className="text-[11px] text-zinc-400">
                              Próximo vencimiento: {formatFechaCorta(r.proximaVence)}
                            </Text>
                          )}
                        </TableCell>
                        {isAll && (
                          <TableCell className="py-3">
                            <Text className="text-sm text-zinc-700 truncate max-w-[180px]" title={r.convenioNombre}>
                              {r.convenioNombre}
                            </Text>
                          </TableCell>
                        )}
                        <TableCell className="py-3">
                          <Text
                            className="text-sm text-zinc-700 truncate max-w-[200px]"
                            title={r.beneficiarios.join(', ')}
                          >
                            {r.beneficiarios.length === 0
                              ? '-'
                              : r.beneficiarios.length === 1
                              ? r.beneficiarios[0]
                              : `${r.beneficiarios.length} beneficiarios`}
                          </Text>
                        </TableCell>
                        <TableCell className="py-3 text-right tabular-nums text-sm text-zinc-700">
                          {r.contracts.length}
                        </TableCell>
                        <TableCell className="py-3 text-right tabular-nums text-sm text-zinc-700">
                          {fmtMXN(r.contratado)}
                        </TableCell>
                        <TableCell className="py-3 text-right tabular-nums text-sm font-semibold text-azul-surelia">
                          {fmtMXN(r.garantizado)}
                        </TableCell>
                        <TableCell className="py-3 text-right tabular-nums text-sm text-zinc-700">
                          {r.totals.fianzasPorEmitir || '-'}
                          {r.totals.fianzasFuturas > 0 && (
                            <span className="text-zinc-400" title="Exigibles al terminar el contrato">
                              {' '}
                              (+{r.totals.fianzasFuturas} fut.)
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          <Text
                            className={`text-sm tabular-nums ${
                              r.atrasadas > 0 ? 'font-semibold text-red-600' : 'text-zinc-400'
                            }`}
                          >
                            {r.atrasadas || '-'}
                          </Text>
                        </TableCell>
                        <TableCell className="py-3 text-right">
                          <Text className="text-sm tabular-nums text-zinc-700">
                            {r.porVencer[30]} / {r.porVencer[60]} / {r.porVencer[90]}
                          </Text>
                        </TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow className="bg-azul-surelia/5">
                          <TableCell colSpan={colSpan} className="py-2 px-6">
                            {/* Fiado → contratos → fianzas. Cada nivel se abre
                                por separado: el proyecto dice cuánto falta, el
                                fiado quién lo debe, el contrato cuál es y la
                                fianza qué lo cubre. */}
                            <div className="sticky left-0 space-y-1">
                              {r.proveedores.length === 0 && (
                                <Text className="text-sm text-zinc-500">Sin proveedores en el proyecto.</Text>
                              )}
                              {r.proveedores.map((p) => {
                                const provKey = `${r.key}:${p.key}`;
                                const provOpen = expandedProveedor.has(provKey);
                                const contratosDelProveedor = r.contracts.filter((c) =>
                                  p.contracts.includes(c.id)
                                );
                                return (
                                  <div key={provKey} className="rounded-lg border border-zinc-200 bg-white">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleProveedor(provKey);
                                      }}
                                      className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 text-left px-3 py-1.5 hover:bg-azul-surelia/5 rounded-lg transition-colors"
                                    >
                                      {provOpen ? (
                                        <ChevronDownIcon className="w-4 h-4 text-zinc-400 shrink-0" />
                                      ) : (
                                        <ChevronRightIcon className="w-4 h-4 text-zinc-400 shrink-0" />
                                      )}
                                      <span className="text-sm font-medium text-azul-surelia min-w-[220px] truncate" title={p.label}>
                                        {p.label}
                                      </span>
                                      <span className="text-xs text-zinc-500">
                                        {contratosDelProveedor.length} contrato
                                        {contratosDelProveedor.length !== 1 ? 's' : ''}
                                      </span>
                                      <span className="text-xs text-zinc-500">{p.counts.vigente} vigentes</span>
                                      {p.counts.overdue > 0 && (
                                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                                          {p.counts.overdue} atrasada{p.counts.overdue !== 1 ? 's' : ''}
                                        </span>
                                      )}
                                      {p.counts.porEmitir > 0 && (
                                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                                          {p.counts.porEmitir} por emitir
                                        </span>
                                      )}
                                      <span className="text-xs text-zinc-600 tabular-nums ml-auto" title="Garantizado vigente">
                                        {fmtMXN(p.montos.vigente)}
                                      </span>
                                    </button>

                                    {provOpen && (
                                      <div className="px-3 pb-2 pl-9 space-y-1">
                                        {contratosDelProveedor.map((c) => {
                                          const contratoOpen = expandedContrato.has(c.id);
                                          const fianzas = c.fianzas || [];
                                          const pendientes = (c.fianzasRequeridas?.value || []).filter(
                                            (fr) => !fr.fianzaId
                                          );
                                          const pendientesDelContrato = p.pendientes.filter(
                                            (fr) => fr.contractId === c.id
                                          );
                                          return (
                                            <div key={c.id} className="rounded-lg border border-zinc-200 bg-white">
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  toggleContrato(c.id);
                                                }}
                                                className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 text-left px-3 py-1.5 hover:bg-azul-surelia/5 rounded-lg transition-colors"
                                              >
                                                {contratoOpen ? (
                                                  <ChevronDownIcon className="w-4 h-4 text-zinc-400 shrink-0" />
                                                ) : (
                                                  <ChevronRightIcon className="w-4 h-4 text-zinc-400 shrink-0" />
                                                )}
                                                <span className="text-sm text-zinc-800 min-w-[180px] truncate" title={c.tituloContrato?.value || ''}>
                                                  {c.numeroContrato?.value || c.tituloContrato?.value || 'Sin número'}
                                                </span>
                                                <span className="text-xs text-zinc-500">
                                                  {fianzas.length} emitida{fianzas.length !== 1 ? 's' : ''}
                                                </span>
                                                {pendientes.length > 0 && (
                                                  <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                                                    {pendientes.length} pendiente{pendientes.length !== 1 ? 's' : ''}
                                                  </span>
                                                )}
                                                <span
                                                  className="text-zinc-400 hover:text-azul-surelia"
                                                  title="Abrir el convenio"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/app/convenios/${r.convenioId}?tab=contratos`);
                                                  }}
                                                >
                                                  <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                                                </span>
                                                <span className="text-xs text-zinc-600 tabular-nums ml-auto">
                                                  {fmtMXN(montoContrato(c))}
                                                </span>
                                              </button>

                                              {contratoOpen && (
                                                <div className="px-3 pb-2 pl-9 space-y-1">
                                                  {fianzas.length === 0 && pendientesDelContrato.length === 0 && (
                                                    <Text className="text-xs text-zinc-500 py-1">
                                                      El contrato no tiene fianzas ni requisitos.
                                                    </Text>
                                                  )}
                                                  {fianzas.map((f) => {
                                                    const status = getFianzaStatus(f);
                                                    const fin = finMs(f);
                                                    return (
                                                      <button
                                                        key={f.id}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          openFianza(f, contractsById?.[c.id] || c);
                                                        }}
                                                        className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 text-left bg-zinc-50 rounded-lg border border-zinc-200 px-3 py-1.5 hover:border-azul-surelia/40 hover:bg-white transition-colors"
                                                      >
                                                        <span className="text-xs font-mono text-zinc-700 min-w-[150px]">
                                                          {formatNumeroFianza(f, 'Sin número')}
                                                        </span>
                                                        <span className="text-xs text-zinc-500 min-w-[120px] truncate">
                                                          {f.tipoFianza || 'Sin tipo'}
                                                        </span>
                                                        <span
                                                          className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                                                            status.code === 'vigente'
                                                              ? 'bg-green-100 text-green-800'
                                                              : status.code === 'vencida'
                                                              ? 'bg-red-100 text-red-800'
                                                              : 'bg-gray-100 text-gray-700'
                                                          }`}
                                                        >
                                                          {status.label}
                                                        </span>
                                                        {fin > 0 && (
                                                          <span className="text-xs text-zinc-400">
                                                            Vence {formatFechaCorta(fin)}
                                                          </span>
                                                        )}
                                                        <span className="text-xs text-zinc-600 tabular-nums ml-auto">
                                                          {fmtMXN(f.montoAfianzado)}
                                                        </span>
                                                      </button>
                                                    );
                                                  })}
                                                  {pendientesDelContrato.map((fr) => (
                                                    <div
                                                      key={fr._key}
                                                      className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 bg-amber-50 rounded-lg border border-amber-200 px-3 py-1.5"
                                                    >
                                                      <span className="text-xs text-amber-900 min-w-[150px]">{fr.tipo}</span>
                                                      <span className="text-xs text-amber-800">Sin emitir</span>
                                                      <span
                                                        className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                                                          fr.isOverdue
                                                            ? 'bg-red-100 text-red-800'
                                                            : 'bg-amber-100 text-amber-800'
                                                        }`}
                                                      >
                                                        {dueLabel(fr)}
                                                      </span>
                                                      <span className="text-xs text-amber-900 tabular-nums ml-auto">
                                                        {fmtMXN(fr.monto)}
                                                      </span>
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <TablePager total={rows.length} page={page} onPage={setPage} perPage={perPage} itemLabel="proyectos" />
        </>
      )}

      {selectedFianza &&
        createPortal(
          <FianzaView
            isOpen={!!selectedFianza}
            onClose={() => setSelectedFianza(null)}
            fianza={selectedFianza}
            clientData={{}}
            contratoData={selectedFianza._contrato}
            onUpdate={() => {}}
          />,
          document.body
        )}
    </div>
  );
}
